import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpInterceptor,
  HttpStatusCode
} from '@angular/common/http';

/** Set to true on a request's HttpContext to suppress the global error toast,
 * when the caller shows its own failure popup instead (e.g. user import). */
export const SKIP_ERROR_NOTIFICATION = new HttpContextToken<boolean>(() => false);

import { merge, Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, retry, switchMap, takeUntil } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { NotificationRef, NotificationService } from "@progress/kendo-angular-notification";
import { errorMessages } from '../constants/error.constant';
import { NavigationService, OktaWrapperService } from '../services/framework';

@Injectable()

export class ErrorIntercept implements HttpInterceptor {

  destroy$ = new Subject();

  private notifications: NotificationRef[] = [];

  constructor(private notificationService: NotificationService
    , private navigationService: NavigationService
    , private oktaWrapperService: OktaWrapperService) {
    this.subscribeToNavigation();
  }

  private subscribeToNavigation(): void {
    merge(
      this.navigationService.onNavigationStart(),
      this.destroy$
    ).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.hideNotifications()),
      filter(() => this.notifications.length > 0)
    ).subscribe();
  }

  private hideNotifications(): Observable<void> {
    const hideObservables: Observable<void>[] = this.notifications.map(notification => {
      return new Observable<void>(observer => {
        notification.hide();
        observer.next();
        observer.complete();
      });
    });

    return merge(...hideObservables);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          let errorMessage = '';

          if (error.error instanceof ErrorEvent) {
            // client-side error
            errorMessage = `Error: ${error.error.message}`;
          } else {
            // server-side error
            errorMessage = `Error Status: ${error.status}\nMessage: ${error.message}`;
          }
          if (!request.context.get(SKIP_ERROR_NOTIFICATION)) {
            this.handleHttpError(error.status, error.message);
          }
          //console.log(errorMessage);

          return throwError(errorMessage);
        })
      )
  }

  private handleHttpError(status: HttpStatusCode, msgStr: string): void {
    if (status === HttpStatusCode.Unauthorized) {
      this.notificationService.show({
        content: 'Your session has expired. Please sign in again.',
        type: { style: 'info', icon: true }
      });
      this.oktaWrapperService.logout();
    }
    // Prefer the friendly, per-status message (e.g. for a 500, "Internal Server
    // Error: An unexpected error occurred on the server.") over msgStr, which is
    // Angular's raw, technical HttpErrorResponse.message (e.g. "Http failure
    // response for https://.../add-users: 500 Internal Server Error") - msgStr is
    // essentially never empty, so it was always winning here and showing users
    // the raw technical string instead of a clean one. Only fall back to msgStr
    // for status codes with no friendly message defined.
    const errorMessageToShow: string = this.getCustomErrorMessage(status) || msgStr || errorMessages.CUSTOM;
    this.show(errorMessageToShow);
  }

  private getCustomErrorMessage(status: HttpStatusCode): string | null {
    switch (status) {
      case HttpStatusCode.BadRequest:
        return errorMessages.BAD_REQUEST;
      case HttpStatusCode.Unauthorized:
        return errorMessages.UNAUTHORIZED;
      case HttpStatusCode.NotFound:
        return errorMessages.NOT_FOUND;
      case HttpStatusCode.InternalServerError:
        return errorMessages.INTERNAL_SERVER;
      // Add other custom status codes and their corresponding error messages here
      default:
        return null; // Return null for unknown status codes
    }
  }

  private show(contentStr: string): void {

    const notification = this.notificationService.show({
      content: contentStr
      , cssClass: "button-notification"
      , animation: { type: "slide", duration: 400 }
      , position: { horizontal: "right", vertical: "bottom" }
      , type: { style: "error", icon: true }
      , closable: true
    });

    this.notifications.push(notification);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
